/**
 * 에러 코드 → 화면 문구.
 *
 * 🔴 사전이 카탈로그의 사본이 아니다. 근거:
 *   - `specs/error_codes.yaml` 의 각 코드는 이미 **한글 `message`** 를 들고 있고,
 *     `ErrorResponse.message`(orchestrator_api.openapi.yaml:639)가 그 문구를 그대로 실어 온다.
 *     147종을 프론트에 복사하면 두 벌이 되고 반드시 어긋난다.
 *   - 다만 카탈로그 문구 중 일부는 **규칙 ID 를 노출**한다(예: `WEAK_PASSWORD` 의 `UAM-PWD-01`,
 *     `REQUEST_VALIDATION_FAIL` 의 `IFC-COM-08`). 와이어프레임 README §1 이 금지한 것이라
 *     그대로 띄울 수 없다. → **덮어쓰기 목록**이 필요하다.
 *
 */
import { ERROR_MESSAGES_EN } from './messages/errors.en.ts'
import { getLocale, hasLocalizedMessage, hasMessage, t } from './t.ts'

/** ErrorResponse(IFC-MSG-03)에서 문구 판정에 필요한 만큼만. 전체 타입은 계약 생성 타입이 온 뒤 대체한다. */
export interface ErrorLike {
  error_code?: string | undefined
  message?: string | undefined
  /** HTTP 상태. 계약 봉투를 벗어난 응답(404 등)의 문구를 고를 때만 본다 */
  status?: number | undefined
}

/**
 * 코드로 찾는 **에러 카탈로그 문구의 지금 언어 판**. 한국어이거나 옮긴 글이 없으면 `undefined` —
 * 부르는 쪽이 한글 카탈로그 · 서버 문구로 떨어진다(생성물 `errorCatalog.generated.ts` 는 고치지 않는다).
 */
export function errorCatalogMessage(code: string): string | undefined {
  if (getLocale() !== 'en' || !Object.hasOwn(ERROR_MESSAGES_EN, code)) return undefined
  return ERROR_MESSAGES_EN[code]
}

/** 화면에 띄울 한 줄. 에러 코드 자체는 절대 문구에 넣지 않는다(§1 규칙 ID·코드 노출 금지). */
export function errorMessage(error: ErrorLike | null | undefined): string {
  if (!error) return t('error.unknown')

  const code = error.error_code
  const fromServer = error.message?.trim()
  if (
    fromServer &&
    fromServer !== code &&
    (code === 'CENT_IMBALANCE' || code === 'RECIPE_VALIDATION_FAIL')
  )
    return fromServer
  if (code) {
    /*
     * 영어 — ① 영어 사전의 `error.<CODE>` ② 카탈로그 영어 판 ③ 그 뒤는 한글(덮어쓰기 → 서버 문구) 순서다.
     * 한국어에서는 ② 가 늘 비어 종전과 같다.
     */
    const key = `error.${code}`
    if (hasMessage(key) && hasLocalizedMessage(key)) return t(key)
    const catalog = errorCatalogMessage(code)
    if (catalog !== undefined) return catalog
    if (hasMessage(key)) return t(key)
  }

  /*
   * 🔴 **계약 봉투를 벗어난 응답만 상태 코드로 말한다**(2026-09-22 실측 사고).
   *    `GET /batches/<없는 id>` 는 `{"detail":"Not Found"}` 라 `error_code` 가 없고,
   *    아래 `fromServer` 가 그 **영문 「Not Found」를 그대로** 화면에 올리고 있었다.
   * 🔴 `error_code` 가 있으면 여기 오지 않는다 — 서버가 준 한글 까닭이 언제나 이긴다.
   *    상태 코드는 「무슨 일인지조차 안 알려 준 응답」의 마지막 수단이다.
   */
  // ⚠ 조건에 `!code` 가 빠져 있어 사전에 없는 코드(`DB_WRITE_FAIL` 등)의 한글 까닭이 상태 코드
  //   문구에 덮였다(2026-09-29 · 시료 중복 등록이 「DB 기록 실패」 대신 「서버가 요청을 처리하지 못했습니다」)
  if (!code && error.status !== undefined) {
    const key = `error.http${String(error.status)}`
    if (hasMessage(key)) return t(key)
  }

  if (fromServer) return fromServer

  return t('error.unknown')
}

/** 응답 자체를 못 받은 경우(네트워크·구독 단절). */
export function networkErrorMessage(): string {
  return t('error.network')
}
